import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDollarSign,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";

// -----------------------------
// Reusable Components
// -----------------------------
// const BackButton = ({ to, text }) => (
//   <Link to={to} className="text-decoration-none">
//     <button className="btn btn-outline-dark d-flex align-items-center gap-2 mb-4">
//       <FontAwesomeIcon icon={faArrowLeft} />
//       {text}
//     </button>
//   </Link>
// );
const BackButton = ({ to, text }) => (
  <Link to={to} className="text-decoration-none">
    <button 
      className="btn btn-outline-dark d-flex align-items-center gap-2 mb-4"
      style={{
        padding: "8px 16px",
        fontSize: "0.95rem",
        minWidth: "auto",
        height: "42px"
      }}
    >
      <FontAwesomeIcon icon={faArrowLeft} />
      {text}
    </button>
  </Link>
);

const InfoCard = ({ title, value, icon, color }) => (
  <div className="col-xl-4 col-md-6 col-sm-12 mb-4">
    <div
      className="card text-white rounded-4 shadow h-100"
      style={{ backgroundColor: color }}
    >
      <div className="card-body text-center py-4">
        <div className="mb-3 fs-2">
          <FontAwesomeIcon icon={icon} />
        </div>
        <h6 className="text-light opacity-75 mb-2">{title}</h6>
        <h2 className="fw-bold mb-0">{value}</h2>
      </div>
    </div>
  </div>
);

const DepartmentCard = ({ dept, onClick }) => {
  const formatSSP = (amt) => `SSP ${Number(amt || 0).toLocaleString()}`;
  const formatUSD = (amt) =>
    `$${Number(amt || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const colors = ["#1E40AF", "#047857", "#B45309", "#9333EA", "#D97706"];
  const idx = Math.floor(Math.random() * colors.length);

  return (
    <div className="col-xl-4 col-lg-4 col-md-6 mb-4">
      <div
        className="card shadow-lg cursor-pointer rounded-4 text-white h-100 d-flex flex-column justify-content-center align-items-center p-4 hover-scale"
        style={{ 
          backgroundColor: colors[idx], 
          transition: "all 0.3s ease",
          minHeight: "180px"
        }}
        onClick={onClick}
      >
        <h5 className="text-uppercase fw-bold mb-3 text-center">{dept.department_name}</h5>
        <h3 className="fw-bold mb-1">{formatSSP(dept.total_ssp)}</h3>
        <p className="mb-2 fs-5">{formatUSD(dept.total_usd)}</p>
        <small className="opacity-75">{dept.transaction_count} transactions</small>
      </div>
    </div>
  );
};

// -----------------------------
// Main Component
// -----------------------------
const AllRevenueReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  ).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const res = await axios.get(
        `${config.smsUrl}/creport/all_transactions_report_v3`,
        { params }
      );
      setReport(res.data);
      actions.snackbarActions.successSnackbar?.("Revenue Report Loaded");
    } catch (err) {
      console.error(err);
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.start_date, filters.end_date]);

  const { summary = {}, department_summary = [], data = [], generated_at } = report || {};

  const sortedDepartments = useMemo(() => 
    [...department_summary].sort((a, b) => (b.total_ssp || 0) - (a.total_ssp || 0)),
    [department_summary]
  );

  const filteredTransactions = useMemo(() => {
    let tx = data || [];
    if (selectedDept) {
      tx = tx.filter((item) => item.department_name === selectedDept);
    }
    if (filters.search.trim()) {
      const term = filters.search.toLowerCase();
      tx = tx.filter((item) =>
        (item.department_name || "").toLowerCase().includes(term) ||
        (item.item_name || "").toLowerCase().includes(term) ||
        (item.patient_name || "").toLowerCase().includes(term) ||
        String(item.visit_id || "").includes(term)
      );
    }
    return tx;
  }, [data, selectedDept, filters.search]);

  const formatSSP = (amt) => `SSP ${Number(amt || 0).toLocaleString()}`;
  const formatUSD = (amt) =>
    `$${Number(amt || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="container-fluid py-4">
      <MainSnackbar />

      <BackButton to="/reports/generalreports" text="Back to Reports" />

      {/* IMPROVED FILTERS */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Search</label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faUsers} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search patient, service, visit ID..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchReport}>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-5">
          <LoadingGif />
          <p className="text-muted mt-3">Loading revenue data...</p>
        </div>
      )}

      {/* GRAND SUMMARY */}
      {!loading && report && (
        <div className="row g-4 mb-5">
          <InfoCard
            title="Total SSP Revenue"
            value={formatSSP(summary.total_revenue_ssp)}
            icon={faDollarSign}
            color="#1E3A8A"
          />
          <InfoCard
            title="Total USD Revenue"
            value={formatUSD(summary.total_revenue_usd)}
            icon={faDollarSign}
            color="#047857"
          />
          <InfoCard
            title="Total Transactions"
            value={summary.total_transactions || 0}
            icon={faUsers}
            color="#B45309"
          />
        </div>
      )}

      {/* DEPARTMENT OVERVIEW - Now Evenly Spaced */}
      {!selectedDept && !loading && report && (
        <div className="row g-4">
          {sortedDepartments.map((dept) => (
            <DepartmentCard
              key={dept.department_name}
              dept={dept}
              onClick={() => setSelectedDept(dept.department_name)}
            />
          ))}
        </div>
      )}

      {/* TRANSACTIONS TABLE */}
      {selectedDept && !loading && (
        <div className="mt-4">
          <button
            className="btn btn-outline-dark mb-3"
            onClick={() => setSelectedDept(null)}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Overview
          </button>
          <div className="card shadow-sm rounded-3 overflow-auto">
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Visit ID</th>
                    <th>Patient</th>
                    <th>Item / Service</th>
                    <th>Qty</th>
                    <th>SSP Revenue</th>
                    <th>USD Revenue</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{new Date(item.sale_date).toLocaleDateString()}</td>
                      <td>{item.visit_id}</td>
                      <td>{item.patient_name || "Walk-in"}</td>
                      <td>{item.item_name}</td>
                      <td>{item.quantity}</td>
                      <td>{formatSSP(item.revenue_ssp)}</td>
                      <td>{formatUSD(item.revenue_usd)}</td>
                      <td>{item.payment_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mt-5 text-muted small">
        Generated: {generated_at || "Just now"} | Period: {filters.start_date} — {filters.end_date}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(null, mapDispatchToProps)(AllRevenueReport);